import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import _ from 'lodash'

import { getDependDataPayload } from '../../../CMSComponent/Forms/Fields/select'
import { getPublicCheckoutContactEmail, readBookingSession } from '../../../Helper/publicPageHelper'
import { setNotification } from '../../../Redux/Reducers/notificationHandling'
import { request } from '../../../Redux/Sagas/requests/api'
import { RiDoorLine } from 'react-icons/ri'
import LockerQuestion from './lockerQuestion'
import { primary_color } from '../../../Helper/uiHelper'
import Loader from 'react-loaders'
import useMediaQuery from '../../../CustomHook/useMediaQuery'
import { Modal, ModalBody } from 'reactstrap'

const createDefaultAnswersNew = (questions) => {
  const obj = {}
  questions.forEach((e) => (obj[e.uuid] = e.answer || ''))
  return obj
}

const normalizeQuestions = (rawQuestions) => {
  if (Array.isArray(rawQuestions)) return rawQuestions
  if (Array.isArray(rawQuestions?.data)) return rawQuestions.data
  return []
}

const questionLabelKey = (q) => (q?.question || '').trim().toLowerCase()

/**
 * Collect answers from booking payload, form row (array or uuid→answer object), and map object keys through
 * the previous template so label-based merge survives UUID changes when year/block APIs return new ids.
 */
const collectPreservedAnswers = (unitRaw, formRow, previousTemplateQuestions) => {
  const byUuid = {}
  const byLabel = {}

  const setNonEmpty = (uuid, labelKey, answer) => {
    if (answer == null || `${answer}` === '') return
    if (uuid != null) byUuid[uuid] = answer
    if (labelKey) byLabel[labelKey] = answer
  }

  const ingestFromArray = (arr) => {
    normalizeQuestions(arr).forEach((q) => {
      setNonEmpty(q?.uuid, questionLabelKey(q), q.answer ?? '')
    })
  }

  ingestFromArray(unitRaw)

  const fq = formRow?.questions
  if (Array.isArray(fq)) {
    ingestFromArray(fq)
  } else if (fq && typeof fq === 'object') {
    if (previousTemplateQuestions?.length) {
      normalizeQuestions(previousTemplateQuestions).forEach((q) => {
        const k = questionLabelKey(q)
        if (!k || !q.uuid || !Object.prototype.hasOwnProperty.call(fq, q.uuid)) return
        const raw = fq[q.uuid]
        const a = raw != null && typeof raw === 'object' && raw.answer !== undefined ? raw.answer : raw
        byLabel[k] = a == null ? '' : `${a}`
      })
    }
    Object.entries(fq).forEach(([uuid, val]) => {
      const a = val != null && typeof val === 'object' && val.answer !== undefined ? val.answer : val
      byUuid[uuid] = a == null ? '' : `${a}`
    })
  }

  return { byUuid, byLabel }
}

const applyPreservedAnswersToTemplate = (templateQuestions, preserved) => {
  const template = normalizeQuestions(templateQuestions)
  if (!template.length) return []
  return template.map((q) => {
    const k = questionLabelKey(q)
    const fromUuid = preserved.byUuid[q.uuid]
    const fromLabel = k ? preserved.byLabel[k] : undefined
    let answer = q.answer ?? ''
    if (fromUuid !== undefined) answer = fromUuid
    else if (fromLabel !== undefined) answer = fromLabel
    return { ...q, answer }
  })
}

const filterAllLocker = (lockers) => {
  const data = JSON.parse(JSON.stringify(lockers))

  const checkCondition = (item) => {
    if (item?.completed || item?.reserved || item?.display == 0) {
      return true
    } else {
      return false
    }
  }

  const maxLength = Math.max(...data.map((row) => row.length))

  const indexToRemove = []

  for (let i = 0; i < maxLength; i++) {
    const shouldRemoveIndex = data.every((row) => {
      return checkCondition(row[i])
    })

    if (shouldRemoveIndex) {
      indexToRemove?.push(i)
    }
  }
  const newData = []

  data.forEach((row) => {
    // return row.splice(i, 1)
    const newRow = []
    row.map((item, index) => {
      if (!indexToRemove?.includes(index)) {
        newRow.push(item)
      }
    })
    newData.push(newRow)
  })

  return newData
}

const LockerBooking = ({
  data,
  formFieldValue = {},
  setFormFieldValue,
  parentData,
  form,
  validationError,
  setValidationError,
  allFormsData,
  unitData,
  setUnitData,
  formIndex,
  setFormData,
  disabledEmptyLocker,
  isBookingPage,
}) => {
  const dispatch = useDispatch()
  const publicUser = useSelector((state) => state.User.publicUser)
  const isDesktop = useMediaQuery('(min-width:768px)')

  const location = useLocation()
  const searchParams = new URLSearchParams(location.search)
  const payment_id = searchParams.get('payment_id')
  const isEditingLocker = searchParams.get('edit_booking')
  const isOldBooking = searchParams.get('old_booking')
  const isRetrieveBooking = searchParams.get('retrieve_booking')
  const shouldPreserveQuestionAnswers = !!(isEditingLocker || isOldBooking || isRetrieveBooking || payment_id)
  const [selectedLocker, setSelectedLocker] = useState()
  const [lockers, setLockers] = useState([])
  const [filteredLocker, setFilteredLocker] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [questions, setQuestions] = useState([])
  const lockerColWidth = isDesktop ? 131.3 : 99.2
  const [lockerColCount, setLocakerCoCount] = useState(0)
  const [isCheckingLockerAvaibility, setIsCheckingLockerAvaibility] = useState(false)
  const [showAllLocker, setShowAllLocker] = useState(false)
  const unitDataRef = useRef(unitData)
  const allFormsDataRef = useRef(allFormsData)
  const questionsRef = useRef(questions)
  useEffect(() => {
    unitDataRef.current = unitData
  }, [unitData])
  useEffect(() => {
    allFormsDataRef.current = allFormsData
  }, [allFormsData])
  useEffect(() => {
    questionsRef.current = questions
  }, [questions])

  useEffect(() => {
    if (unitData?.length && unitData[formIndex]) {
      const unitQuestions = normalizeQuestions(unitData[formIndex].questions)
      // Do not overwrite selected questions with empty unit data payload.
      if (unitQuestions.length) {
        setQuestions(unitQuestions)
      }
    }
  }, [JSON.stringify(unitData), formIndex])

  const dependent_value = formFieldValue?.[data?.component_call_data_connection?.source_field]

  //* Convert API Data into vertical Array
  const displayVertically = (arr, rowCount) => {
    const output = arr.reduce((rows, item, index) => {
      const rowIndex = index % rowCount
      rows[rowIndex] = rows[rowIndex] || []
      rows[rowIndex].push(item)
      return rows
    }, [])

    return output
  }

  const fetchData = async (payload) => {
    setIsLoading(true)
    try {
      const response = await request('get-template-data-depend', 'POST', { ...payload })
      if (response?.status === 200 || response?.status === 202) {
        // const new_lockers = []
        const formattedData = displayVertically(response?.data?.data, response?.data?.lockerHeight)
        const apiQuestions = normalizeQuestions(response?.data?.questions)
        const formRow = allFormsDataRef.current?.[formIndex]
        const unitRaw = unitDataRef.current?.[formIndex]?.questions
        const preserved = collectPreservedAnswers(unitRaw, formRow, questionsRef.current)
        const hasExistingAnswers = Object.keys(preserved?.byUuid || {}).length > 0 || Object.keys(preserved?.byLabel || {}).length > 0
        let nextQuestions = apiQuestions
        if (shouldPreserveQuestionAnswers || hasExistingAnswers) {
          nextQuestions = apiQuestions.length ? applyPreservedAnswersToTemplate(apiQuestions, preserved) : normalizeQuestions(unitRaw)
          if (!nextQuestions.length && formRow?.questions) {
            nextQuestions = normalizeQuestions(formRow.questions)
          }
        }
        setQuestions(nextQuestions)

        const new_lockers = formattedData?.map((item) => {
          return item?.map((dataItem) => {
            return {
              id: dataItem?.id,
              reserved: dataItem?.completed || dataItem?.reserved || dataItem?.off_sale || dataItem?.is_maintenance ? true : false,
              display: dataItem?.display,
              item_name: dataItem?.name,
              price: dataItem?.rate,
              questions: normalizeQuestions(dataItem?.questions).length ? normalizeQuestions(dataItem?.questions) : apiQuestions,
            }
          })
        })

        setLocakerCoCount(formattedData[0]?.length)
        setLockers(new_lockers)
        const filteredLocker = filterAllLocker(new_lockers)
        if (response?.data?.mapped_location_id && setFormData) {
          const mappedId = response.data.mapped_location_id
          if (mappedId && mappedId !== formFieldValue?.location_id) {
            setFormData((prev) =>
              (prev || []).map((row, i) => {
                if (i !== formIndex) return row
                return {
                  ...row,
                  location_id: mappedId,
                  location_id_label: response.data.mapped_location_name || row.location_id_label,
                }
              })
            )
          }
        }
        let isLockerAvailable = false
        filteredLocker?.map((item) => {
          if (item && item?.length > 0) {
            isLockerAvailable = true
          }
        })
        setFilteredLocker(filteredLocker)
        if (!isLockerAvailable && isEditingLocker) {
          setShowAllLocker(true)
        }
      }
    } catch (e) {
      console.error('Error in get-template-data-depend : ', e)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (!dependent_value) {
      setLockers([])
      setFilteredLocker([])
      setQuestions([])
      return
    }
    let payload = getDependDataPayload(form?.component_call_fields, data?.component_call_data_connection, dependent_value)
    if (formFieldValue?.item_date_time_id) {
      payload = { ...payload, item_date_time_id: formFieldValue.item_date_time_id }
    }
    fetchData(payload)
  }, [dependent_value, formFieldValue?.item_date_time_id])
  const onChangeValue = (reserved, value, item) => {
    const current_storage_timer = readBookingSession(parentData?.id)
    const selected_items = allFormsData?.map((forms) => forms[data?.field_id])
    if (reserved && !unitData.map((e) => e.item_id).includes(item?.id))
      return dispatch(
        setNotification({
          type: 'error',
          message: 'This locker is already reserved.',
        })
      )
    if (selected_items?.includes(item?.id)) {
      dispatch(
        setNotification({
          type: 'error',
          message: 'This locker is already selected.',
        })
      )
      return
    }
    let itemToCheck = {
      ...item,
    }
    if (allFormsData?.[formIndex]) {
      itemToCheck = {
        ...itemToCheck,
        item_date_time_id: allFormsData?.[formIndex]?.item_date_time_id,
        location_id: allFormsData?.[formIndex]?.location_id,
        category_id: allFormsData?.[formIndex]?.category_id,
      }
    }
    const items = allFormsData?.map((item, index) => {
      if (index === formIndex) {
        return {
          ...itemToCheck,
          item_date_time_id: allFormsData?.[formIndex]?.item_date_time_id,
          location_id: allFormsData?.[formIndex]?.location_id,
          category_id: allFormsData?.[formIndex]?.category_id,
        }
      } else {

        return {
          ...item,
          item_date_time_id: allFormsData?.[index]?.item_date_time_id,
          location_id: allFormsData?.[index]?.location_id,
          category_id: allFormsData?.[index]?.category_id,
          id: allFormsData?.[index]?.item_id,
          item_name: allFormsData?.[index]?.item_id_label,
        }
      }
    })
   
    let payload = {
      items: items,
      // items: [item?.id],
      session_id: current_storage_timer?.session_id,
      uuid: formFieldValue?.uuid,
      payment_id: payment_id && parseInt(payment_id),
      email: (publicUser?.email && String(publicUser.email).trim()) || getPublicCheckoutContactEmail().trim(),
    }
    setIsCheckingLockerAvaibility(true)
    request('check-item-availability', 'POST', payload).then((res) => {
      setIsCheckingLockerAvaibility(false)
      if (res.status === 200 || res?.status === 202) {
        setSelectedLocker(value)
        setValidationError(false)
        const selectedQuestionsRaw = normalizeQuestions(item?.questions)
        const formRow = allFormsDataRef.current?.[formIndex]
        const preserved = collectPreservedAnswers(
          unitDataRef.current?.[formIndex]?.questions,
          formRow,
          questionsRef.current
        )
        const mergedQuestions = selectedQuestionsRaw.length
          ? applyPreservedAnswersToTemplate(selectedQuestionsRaw, preserved)
          : []
        setQuestions(mergedQuestions)
        let updatedValue = {
          ...formFieldValue,
        }
        updatedValue[data?.field_id] = value
        if (!mergedQuestions.length) {
          delete updatedValue['questions']
        }
        setFormData((prevFormsData) => {
          const newData = prevFormsData.map((ele, i) => {
            if (formIndex === i) {
              let newdata = {
                ...ele,
              }
              newdata[data?.field_id] = item?.id
              if (isEditingLocker) {
                newdata['old_item_id'] = unitData?.[formIndex]?.item_id
              }

              if (mergedQuestions.length) {
                newdata['questions'] = createDefaultAnswersNew(mergedQuestions)
              } else {
                delete newdata['questions']
              }
              // newdata['questions'] = createDefaultAnswersNew(isEditingLocker ? questions : item?.questions, isEditingLocker)
              return newdata
            } else {
              return ele
            }
          })
          return newData
        })
        // setFormFieldValue({...updatedValue , questions : createDefaultAnswersNew(item?.questions)})
      } else {
        dispatch(
          setNotification({
            type: 'error',
            message: res?.data?.message,
          })
        )
        let payload = getDependDataPayload(form?.component_call_fields, data?.component_call_data_connection, dependent_value)
        fetchData(payload)
      }
    })
  }

  const getBgColor = (item) => {
    const selected_items = allFormsData?.map((forms) => forms[data?.field_id])
    const unitDataItemId = unitData?.map((item) => item?.item_id)

    if (isEditingLocker) {
      if (unitDataItemId?.includes(item?.id) && unitData?.[formIndex]?.item_id === item?.id) {
        return '#059669CC'
      } else if (formFieldValue[data?.field_id] === item?.id) {
        return '#06880BCC'
      } else if (item?.display === 0) {
        return '#FFFFFF'
      } else if ((item?.reserved && !unitData.map((e) => e.item_id).includes(item?.id)) || selected_items?.includes(item?.id)) {
        return '#F87171'
      } else {
        return primary_color
      }
    } else {
      if (formFieldValue[data?.field_id] === item?.id) {
        return '#059669CC'
      } else if (item?.display === 0) {
        return '#FFFFFF'
      } else if ((item?.reserved && !unitData.map((e) => e.item_id).includes(item?.id)) || selected_items?.includes(item?.id)) {
        return '#F87171'
      } else {
        return primary_color
      }
    }
  }

  if (isLoading)
    return (
      <div className='d-flex align-items-center justify-content-center' style={{ height: '10rem' }}>
        <Loader type='ball-pulse' color={primary_color} />
      </div>
    )

  let lockersToShow = filteredLocker
  if (showAllLocker) {
    lockersToShow = lockers
  }

  let isLockerAvailable = false
  filteredLocker?.map((item) => {
    if (item && item?.length > 0) {
      isLockerAvailable = true
    }
  })

  if (lockers?.length === 0 && dependent_value)
    return (
      <div className='d-flex align-items-center justify-content-center' style={{ height: '10rem' }}>
        No lockers found.
      </div>
    )

  if (!isLockerAvailable && !showAllLocker && dependent_value)
    return (
      <div>
        <button
          type='button'
          className='btn rounded-pill mt-2 subtitle1 font-weight-bold mb-3 mt-5'
          style={{ color: primary_color, width: 'fit-content', paddingInline: '1rem', border: `1px solid ${primary_color}` }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            setShowAllLocker(!showAllLocker)
          }}>
          {showAllLocker ? 'Show Available Lockers' : 'Show All Lockers'}
        </button>
        <div className='d-flex align-items-center justify-content-center' style={{ height: '10rem' }}>
          No lockers found.
        </div>
      </div>
    )
  return (
    <div className='mt-2'>
      {lockersToShow?.length > 0 && (
        <>
          {isEditingLocker ? (
            <div className='d-flex align-items-center mb-5 mt-5 subtitle1'>
              <div className='rounded-pill mr-2 subtitle1' style={{ height: '1rem', width: '1rem', background: '#059669CC' }}></div> Currently Booked
              <div className='rounded-pill mr-2 ml-3 subtitle1' style={{ height: '1rem', width: '1rem', background: primary_color }}></div> Available
              <div className='rounded-pill mr-2 ml-3 subtitle1' style={{ height: '1rem', width: '1rem', background: '#06880BCC' }}></div> Booked
              <div className='rounded-pill mr-2 ml-3 subtitle1' style={{ height: '1rem', width: '1rem', background: '#F87171' }}></div> Not Available
            </div>
          ) : (
            <div className='d-flex align-items-center mb-5 mt-5 subtitle1'>
              <div className='rounded-pill mr-2 subtitle1' style={{ height: '1rem', width: '1rem', background: '#059669CC' }}></div> Selected
              <div className='rounded-pill mr-2 ml-3 subtitle1' style={{ height: '1rem', width: '1rem', background: primary_color }}></div> Available
              <div className='rounded-pill mr-2 ml-3 subtitle1' style={{ height: '1rem', width: '1rem', background: '#F87171' }}></div> Not Available
            </div>
          )}
        </>
      )}
      <div className=' mb-2 !w-100 custom-scrollbar' style={{ overflow: 'auto' }}>
        {lockers?.length > 0 && (
          <div className=''>
            <button
              type='button'
              className='btn rounded-pill mt-2 subtitle1 font-weight-bold mb-3'
              style={{ color: primary_color, width: 'fit-content', paddingInline: '1rem', border: `1px solid ${primary_color}` }}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                setShowAllLocker(!showAllLocker)
              }}>
              {showAllLocker ? 'Show Available Lockers' : 'Show All Lockers'}
            </button>
          </div>
        )}

        {lockersToShow?.map((locakerItem, rowIndex) => {
          return (
            <div key={`locker-row-${rowIndex}`} style={{ width: lockerColWidth * lockerColCount + 'px' }} className='d-flex'>
              {locakerItem?.map((item, colIndex) => {
                const isDisplayZero = item?.display === 0 ? true : false
                return (
                  <div
                    key={item?.id || `${rowIndex}-${colIndex}`}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      if (getBgColor(item) !== primary_color) {
                        return
                      }
                      if (item?.display) {
                        onChangeValue(item?.reserved, item?.id, item)
                      }
                    }}
                    className=' position-relative'
                    style={{
                      width: isDesktop ? '8rem' : '6rem',
                      margin: '0.1rem',
                      height: isDesktop ? '10rem' : '9rem',
                      background: getBgColor(item),
                      cursor: getBgColor(item) == primary_color ? 'pointer' : 'default',
                    }}>
                    {!isDisplayZero ? (
                      <div
                        className='m-1 position-absolute subtitle1'
                        style={{
                          left: '0.5rem',
                          top: '0.25rem',
                          background: 'white',
                          padding: '0rem 0.5rem',
                          borderRadius: '14px',
                          fontWeight: '500',
                        }}>
                        <div>£{item?.price}</div>
                      </div>
                    ) : null}
                    {!!item?.display && (
                      <div className='position-absolute' style={{ top: '50%', left: '50%', transform: 'translate(-50% , -50%)' }}>
                        {/* <i class='fa-thin fa-door-closed' style={{ fontSize: '5rem' }}></i> */}
                        <RiDoorLine style={{ color: '#fff', fontSize: '6rem' }} />
                      </div>
                    )}
                    {!!item?.display && (
                      <div
                        className='text-align-center mt-2 subtitle1'
                        style={{
                          color: '#fff',
                          position: 'absolute',
                          bottom: '1rem',
                          fontWeight: '500',
                          left: '50%',
                          transform: 'translate(-50%)',
                        }}>
                        {item?.item_name}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )
        })}
      </div>

      {questions && questions?.length > 0 && (
        <div className='mb-4 mt-5 body1' style={{ color: primary_color, fontWeight: 700 }}>
          Student Details
        </div>
      )}
      {questions && questions?.length > 0 && (
        <LockerQuestion
          validationError={validationError}
          setValidationError={setValidationError}
          questions={questions}
          formFieldValue={formFieldValue}
          setFormFieldValue={setFormFieldValue}
          formIndex={formIndex}
          setFormData={setFormData}
        />
      )}
      <Modal isOpen={isCheckingLockerAvaibility} centered={true} toggle={() => {}} style={{ width: '30rem', boxShadow: 'none', maxWidth: '95vw' }}>
        <ModalBody>
          <div className='d-flex justify-content-center' style={{ height: '5rem', marginTop: '3rem' }}>
            <Loader type='ball-triangle-path' color={primary_color} />
          </div>

          <div className='text-center body2' style={{ color: primary_color }}>
            Please hold on while we confirm if a locker is available...
          </div>
        </ModalBody>
      </Modal>
    </div>
  )
}

export default LockerBooking
